// The commerce family's `blocker.guidance_written` (#6679) — split out of `commerce.ts`
// purely for size (biome's 800-line file cap), the `commerce-webhook-source.ts` posture:
// this group is spread into `TERMINAL_RENDERERS_COMMERCE`, so the family's partition and
// coverage tests see it where the vocabulary (`../../audit/kind-commerce.ts`, beside the
// other `blocker.*` kinds) puts it.
//
// The output is parsed WHOLE against its writer's schema, the `forge.mr.*` posture: this
// row's whole job is to say which way the attempt went, and a line naming an outcome
// without its count (or claiming `none` with four steps) would be worse than no line. A
// withheld or malformed row renders nothing. Never `'omit'`: an omitted kind is dropped
// from `TERMINAL_KINDS`, and "we tried and got nothing" is exactly the line an operator
// asking why a blocker has no steps is looking for.
//
// ONE LEVEL FOR THE KIND, per kind and never per row (`wire/terminal.ts`): `info`. Nobody
// owes this row an act — the ask it belongs to announced itself when `blocker.asked`
// fired, and the steps are on the blocker. `system`: a cron tick runs the sweep, no human
// does. `decision` SUBJECT, matching every other `blocker.*` row, so an operator reading
// one ask reads its guidance attempt on the same subject.

import type { TerminalRefs, TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { BlockerGuidanceWrittenOutputSchema } from '../../audit/payload';

/**
 * One `blocker.guidance_written` row as its line. `null` when the output does not parse —
 * including the `visibleToMaintainer: false` case, where the terminal query withholds the
 * payload and there is no outcome to name.
 *
 * The blocker id comes off the row's INPUT and is optional there: with no real id of
 * `kind: 'decision'` the subject falls back to the row's session, the same contract
 * `blocker.asked` uses for the identical gap (never the audit row's own uuid, #4445).
 */
function renderBlockerGuidanceWritten(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = BlockerGuidanceWrittenOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { outcome, steps, reason, model } = parsed.data;
  const input =
    typeof ctx.row.input === 'object' && ctx.row.input !== null
      ? (ctx.row.input as Record<string, unknown>)
      : {};
  const id = input['blocker_id'];
  const blockerId = typeof id === 'string' && id.length > 0 ? id : undefined;
  const refs: TerminalRefs = {
    ...(blockerId !== undefined ? { blockerId } : {}),
    ...(ctx.row.repoFullName !== null ? { repoFullName: ctx.row.repoFullName } : {}),
  };
  return {
    line:
      outcome === 'steps'
        ? { key: 'terminal.blocker.guidance_written.steps', params: { steps, model } }
        : {
            key: 'terminal.blocker.guidance_written.none',
            params: { reason: reason ?? '', model },
          },
    subject:
      blockerId !== undefined
        ? { kind: 'decision', id: blockerId, label: blockerId }
        : { kind: 'session', id: ctx.row.sessionId, label: 'blocker' },
    refs,
  };
}

/** The group spread into `TERMINAL_RENDERERS_COMMERCE` — see the file header. */
export const TERMINAL_RENDERERS_BLOCKER_GUIDANCE = terminalRendererGroup({
  'blocker.guidance_written': {
    level: 'info',
    actor: 'system',
    render: renderBlockerGuidanceWritten,
  },
});
